import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { PredominantDegreeCode } from '@backrs_ui/common-ui/src/models/college';
import { getCollegeIcon } from '@backrs_ui/common-ui/src/models/major';
import { ContentNugget, NuggetVariant } from '@backrs_ui/common-ui/src/models/nugget';
import { ToolType } from '@backrs_ui/common-ui/src/models/tool';
import { formatDateShort } from '@backrs_ui/common-ui/src/utils/dateTime.util';

import { resolveMeshConfig } from './meshConfigs';
import type { MeshConfig } from './MeshGradient';
import { NuggetBadgeProps } from './NuggetBadge';
import { getNuggetCardBadge, getFeaturedIconBgColor } from './nuggetCardBadgeMapping.util';

type NuggetCardDataMapperParams = Readonly<{
  nugget: ContentNugget;
  /** Resolved career path icon from getCareerPathIcon() — pass from grow-ui */
  careerPathIcon?: AppIcons;
  /** For institution variant, the predominant degree code (e.g. 'bachelors', 'associates') */
  predominantDegreeCode?: PredominantDegreeCode;
}>;

type FeaturedIconData =
  | { icon: AppIcons; bgColor: ColorToken }
  | { component: 'badgeCircle'; bgColor: ColorToken };

type NuggetCardData = Readonly<{
  imageUrl?: string;
  isVideo?: boolean;
  title: string;
  badge: NuggetBadgeProps;
  entityName: string;
  featuredIcon?: FeaturedIconData;
  description?: string;
  meshConfig: MeshConfig;
}>;

/**
 * Default featured icon per variant (for variants without a dynamic icon).
 */
const STATIC_FEATURED_ICONS: Partial<Record<NuggetVariant, AppIcons>> = {
  competency: 'CheckVerified03',
  'educational-investment': 'Building08',
  industry: 'Building07',
  'deep-dive': 'SearchLg',
  playlist: 'BookOpen01',
  tool: 'Tool02',
  'solution-session': 'Stars01',
  'career-track': 'Route',
};

/**
 * Featured icon overrides per tool_type for tool nuggets.
 * Types not listed use the default tool featured icon.
 */
const TOOL_TYPE_FEATURED_ICONS: Partial<Record<ToolType, { icon: AppIcons; bgColor: ColorToken }>> =
  {
    guide: { icon: 'Certificate02', bgColor: 'Gray/700' },
    'solution-session': { icon: 'Target05', bgColor: 'Brand/50' },
  };

type ResolvedImage = Readonly<{ url?: string; isVideo?: boolean }>;

const IMAGE_PATH_PATTERN = /\.jpe?g|\.png|\.gif|\.webp|\.svg|\.avif|\.bmp|\/images[/%]/i;

/**
 * Checks if a URL likely points to a static image.
 * preview_media can contain videos, PDFs, or other non-image files —
 * we only want to render it in an <img> tag if it's actually an image.
 */
const isLikelyImageUrl = (url: string): boolean => {
  try {
    const pathname = decodeURIComponent(new URL(url).pathname);

    return IMAGE_PATH_PATTERN.test(pathname);
  } catch {
    return IMAGE_PATH_PATTERN.test(url);
  }
};

/**
 * Resolves image URL from a ContentNugget based on its variant.
 * For video-internal nuggets without a preview image, returns the video URL
 * with `isVideo: true` so the card can render a <video> element instead of <img>.
 */
const resolveImageUrl = (nugget: ContentNugget): ResolvedImage => {
  switch (nugget.variant) {
    case 'video-external':
    case 'video-internal': {
      const previewUrl = nugget.preview_media?.media_url;
      const previewIsImage = previewUrl ? isLikelyImageUrl(previewUrl) : false;

      // Use preview_media only if it's actually an image (not a video, PDF, etc.)
      if (previewUrl && previewIsImage) {
        return { url: previewUrl };
      }

      // For external videos, try to extract a platform thumbnail
      if (nugget.variant === 'video-external' && nugget.external_url) {
        const YOUTUBE_REGEX =
          /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)(\/(?:[\w-]+\?v=|embed\/|v\/)?)([\w-]+)(\S+)?$/;
        const youtubeVideoId = nugget.external_url.match(YOUTUBE_REGEX)?.[5];

        if (youtubeVideoId) {
          return { url: `https://img.youtube.com/vi/${youtubeVideoId}/0.jpg` };
        }
      }

      // For any video URL we do have (preview or main media), render as <video>
      const videoUrl = (!previewIsImage ? previewUrl : undefined) ?? nugget.media?.media_url;

      if (videoUrl) {
        return { url: videoUrl, isVideo: true };
      }

      return {};
    }

    case 'institution':
      return { url: nugget.college_info?.banner_media_url ?? nugget.preview_media?.media_url };

    case 'employer':
      return {
        url:
          nugget.employer_info?.banner_media_url ??
          nugget.employer_info?.logo_media_url ??
          nugget.preview_media?.media_url,
      };

    case 'internship':
      return {
        url: nugget.internship_info?.cover_media_url,
      };

    case 'coach':
      return { url: nugget.coach_info?.profile_image_url ?? nugget.preview_media?.media_url };

    case 'deep-dive':
      return { url: nugget.deep_dive_info?.media ?? nugget.preview_media?.media_url };

    case 'playlist':
      return {
        url: nugget.playlist_info?.preview_media?.media_url ?? nugget.preview_media?.media_url,
      };

    case 'career-path':
      return {
        url: nugget.career_path_info?.media_url ?? nugget.preview_media?.media_url,
      };

    case 'educational-investment':
      return {
        url: nugget.educational_investment_info?.media_url ?? nugget.preview_media?.media_url,
      };

    case 'industry':
      return {
        url: nugget.industry_info?.media_url ?? nugget.preview_media?.media_url,
      };

    case 'major':
      return { url: nugget.major_info?.media_url ?? nugget.preview_media?.media_url };

    case 'tool':
      return {
        url:
          nugget.tool_info?.banner_media?.media_url ??
          nugget.tool_info?.media?.media_url ??
          nugget.preview_media?.media_url,
      };

    case 'profile-external':
      return {
        url: nugget.external_profile_info?.profile_image_url ?? nugget.preview_media?.media_url,
      };

    case 'profile-internal':
      return { url: nugget.profile_info?.profile_image ?? nugget.preview_media?.media_url };

    case 'generated-content':
      return {};

    default:
      return { url: nugget.preview_media?.media_url ?? nugget.media?.media_url };
  }
};

/**
 * Resolves entity name for the gradient fallback state.
 */
const resolveEntityName = (nugget: ContentNugget): string => {
  switch (nugget.variant) {
    case 'institution':
      return nugget.college_info?.school_name ?? nugget.title;
    case 'employer':
      return nugget.employer_info?.name ?? nugget.title;
    case 'internship':
      return nugget.internship_info?.company ?? nugget.title;
    case 'coach':
      return nugget.coach_info
        ? `${nugget.coach_info.first_name ?? ''} ${nugget.coach_info.last_name ?? ''}`.trim()
        : nugget.title;
    case 'deep-dive':
      return nugget.deep_dive_info?.name ?? nugget.title;
    case 'playlist':
      return nugget.playlist_info?.title ?? nugget.title;
    case 'scholarships':
      return nugget.scholarship_info?.name ?? nugget.title;
    case 'career-path':
      return nugget.career_path_info?.name ?? nugget.title;
    case 'major':
      return nugget.major_info?.name ?? nugget.title;
    case 'industry':
      return nugget.industry_info?.name ?? nugget.title;
    case 'tool':
      return nugget.tool_info?.title ?? nugget.title;
    case 'track-assessment-unlock':
      return nugget.track_assessment_unlock_info?.category?.shortname ?? nugget.title;
    case 'generated-content':
      return nugget.generated_content_info?.title ?? nugget.title;
    default:
      return nugget.title;
  }
};

/**
 * Maps a ContentNugget to the props needed by NuggetCard.
 */
export const mapNuggetToCardData = ({
  nugget,
  careerPathIcon,
  predominantDegreeCode,
}: NuggetCardDataMapperParams): NuggetCardData => {
  const { url: imageUrl, isVideo } = resolveImageUrl(nugget);
  const entityName = resolveEntityName(nugget);
  const meshConfig = resolveMeshConfig(nugget);

  // Generated content has its own mapping
  if (nugget.variant === 'generated-content') {
    const info = nugget.generated_content_info;
    const description = info ? `Ask Lubav: ${info.summary}` : nugget.title;
    const formattedDate = info?.updated_at ? formatDateShort(info.updated_at) : undefined;

    return {
      imageUrl: undefined,
      title: info?.title ?? nugget.title,
      badge: getNuggetCardBadge({ variant: nugget.variant, formattedDate }),
      entityName,
      featuredIcon: { component: 'badgeCircle', bgColor: 'Beige/25' },
      description,
      meshConfig,
    };
  }

  let title = nugget.title;

  if (nugget.variant === 'deep-dive' && nugget.deep_dive_info?.price != null) {
    title = `${title} — $${nugget.deep_dive_info.price}`;
  }

  // Badge
  const badge = getNuggetCardBadge({
    variant: nugget.variant,
    predominantDegreeCode,
    profileImageUrl:
      nugget.variant === 'text-response' ? nugget.profile_info?.profile_image : undefined,
    roleType: nugget.variant === 'internship' ? nugget.internship_info?.role_type : undefined,
    awardType: nugget.variant === 'scholarships' ? nugget.scholarship_info?.award_type : undefined,
    toolType: nugget.variant === 'tool' ? nugget.tool_info?.tool_type : undefined,
  });

  // Featured icon
  const featuredIconBgColor = getFeaturedIconBgColor(nugget.variant);
  let featuredIcon: NuggetCardData['featuredIcon'] = undefined;

  if (featuredIconBgColor) {
    const toolTypeFeaturedIcon =
      nugget.variant === 'tool' && nugget.tool_info?.tool_type
        ? TOOL_TYPE_FEATURED_ICONS[nugget.tool_info.tool_type]
        : undefined;

    let icon: AppIcons;

    if (nugget.variant === 'career-path' && careerPathIcon) {
      icon = careerPathIcon;
    } else if (nugget.variant === 'major') {
      icon = getCollegeIcon(nugget.major_info?.typicalCollege ?? '');
    } else {
      icon = STATIC_FEATURED_ICONS[nugget.variant] ?? 'InfoCircle';
    }

    featuredIcon = toolTypeFeaturedIcon ?? { icon, bgColor: featuredIconBgColor };
  }

  return {
    imageUrl,
    isVideo,
    title,
    badge,
    entityName,
    featuredIcon,
    meshConfig,
  };
};
